/*
 * Copyright (c) 2025 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import React, { useEffect, useRef } from "react";
import _ from "lodash";

/**
 * A React hook that runs an effect when dependencies change, using deep comparison
 * for specified indices (or all if not provided).
 *
 * @param {React.EffectCallback} callback - The effect callback to run.
 * @param {any[]} dependencies - The dependency array.
 * @param {number[]} [deepCompareIndices] - Optional array of indices to deep compare. If omitted, all are deep compared.
 */
export const useDeepCompareEffect = (
  callback: React.EffectCallback,
  dependencies: any[],
  deepCompareIndices?: number[]
) => {
  const previousDependenciesRef = useRef<any[]>([]);

  // If deepCompareIndices is not provided, use all indices
  const indicesToCompare = React.useMemo(
    () => deepCompareIndices ?? dependencies.map((_, idx) => idx),
    [deepCompareIndices, dependencies]
  );

  useEffect(() => {
    const shouldUpdate = dependencies.some((dep, index) => {
      if (indicesToCompare.includes(index)) {
        return !_.isEqual(previousDependenciesRef.current[index], dep);
      } else {
        return previousDependenciesRef.current[index] !== dep;
      }
    });

    if (shouldUpdate) {
      previousDependenciesRef.current = dependencies.map((dep, index) =>
        indicesToCompare.includes(index) ? _.cloneDeep(dep) : dep
      );
      callback();
    }
  }, [callback, dependencies, indicesToCompare]);
};